import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { State } from 'country-state-city';
import * as Yup from 'yup';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { useSelector } from 'react-redux';
import axiosWrapper from '../../../../utils/api';
import { API_URL } from '../../../../utils/apiUrl';
import manualIcon from '@icons/user-cirlce-add.svg';
import inviteIcon from '@icons/sms-star.svg';
import './AddParkerModal.scss';
import PhoneInput from 'react-phone-input-2';

const currentYear = new Date().getFullYear();

const validationSchema = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string(),
    phoneNumber: Yup.string(),
    notes: Yup.string(),
    vehicles: Yup.array().of(
        Yup.object({
            vehicleMake: Yup.string().required('Vehicle make is required'),
            licensePlate: Yup.string().required('License plate is required'),
            province: Yup.string().required('Province/State is required'),
            model: Yup.string().required('Model is required'),
            year: Yup.number()
                .required('Year is required')
                .min(1886, 'Year must be greater than or equal to 1886')
                .max(currentYear + 1, `Year must be less than or equal to ${currentYear + 1}`),
            color: Yup.string().required('Color is required')
        })
    )
});

const inviteValidationSchema = Yup.object({
    emails: Yup.array()
        .of(
            Yup.object({
                value: Yup.string().email('Invalid email format'),
                label: Yup.string()
            })
        )
        .min(1, 'At least one email is required'),
    notes: Yup.string()
});

const AddParkerModal = ({
    show,
    onHide,
    groupId,
    refreshParkers,
    editMode = false,
    viewMode = false,
    parkerId = null,
    subscriptionId = ''
}) => {
    const [step, setStep] = useState(parkerId ? 'manual' : 'options');
    const [loading, setLoading] = useState(false);
    const [subscriptionOptions, setSubscriptionOptions] = useState([]);
    const { userToken, userInfo } = useSelector((state) => state.auth);
    const states = State.getStatesOfCountry('US').map((state) => ({
        value: state.name,
        label: state.name
    }));

    const initialValueObj = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        notes: '',
        vehicles: [
            {
                vehicleMake: '',
                licensePlate: '',
                province: '',
                model: '',
                year: '',
                color: ''
            }
        ],
        subscriptions:
            userInfo?.role === 'OPERATOR' || userInfo?.role === 'ADMIN' ? [] : subscriptionId ? [subscriptionId] : []
    };

    const [initialValues, setInitialValues] = useState(initialValueObj);

    const inviteInitialValues = {
        emails: [],
        notes: '',
        subscriptions:
            userInfo?.role === 'OPERATOR' || userInfo?.role === 'ADMIN' ? [] : subscriptionId ? [subscriptionId] : []
    };

    // Manual submission: "Save & Close"
    const handleManualSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const parkerData = {
                ...values,
                type: 'MANUAL',
                groupId
            };

            let response;
            if (editMode && parkerId) {
                // If in edit mode, update the Parker
                response = await axiosWrapper(
                    'put',
                    `${API_URL.UPDATE_GROUP_PARKER_BY_ID(parkerId)}`,
                    parkerData,
                    userToken
                );
            } else {
                // Otherwise, create a new Parker
                response = await axiosWrapper('post', API_URL.CREATE_GROUP_PARKER, parkerData, userToken);
            }
            if (response?.success) {
                resetForm();
                onHide();
                refreshParkers(); // refresh table after successful create / update
                setSubmitting(false);
            }
        } catch (error) {
            setSubmitting(false);
            refreshParkers();
            onHide();
        }
    };

    // Manual submission: "Save & Add Another"
    const handleManualSubmitAndAdd = async (values, { setSubmitting, resetForm }) => {
        try {
            const parkerData = {
                ...values,
                type: 'MANUAL',
                groupId
            };
            const response = await axiosWrapper('post', API_URL.CREATE_GROUP_PARKER, parkerData, userToken);
            if (response?.success) {
                resetForm();
                refreshParkers();
                setSubmitting(false);
            }
        } catch (error) {
            setSubmitting(false);
        }
    };

    // Invite submission flow
    const handleInviteSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const emails = values.emails.map((email) => email.value);
            const inviteData = {
                type: 'INVITE',
                emails,
                subscriptions: values.subscriptions,
                groupId,
                notes: values.notes
            };
            const response = await axiosWrapper('post', API_URL.CREATE_GROUP_PARKER, inviteData, userToken);
            if (response?.success) {
                resetForm();
                onHide();
                refreshParkers();
                setSubmitting(false);
            }
        } catch (error) {
            setSubmitting(false);
        }
    };

    // Edit Parker

    // ////// Fetch PArker Information

    const fetchParker = async (id) => {
        setLoading(true);
        try {
            const response = await axiosWrapper('get', `${API_URL.GET_GROUP_ADMIN_PARKER_BY_ID(id)}`, null, userToken);
            if (response?.success) {
                const data = response.data;
                setStep(data?.isInvite ? 'invite' : 'manual');
                const mappedData = {
                    firstName: data?.firstName || '',
                    lastName: data?.lastName || '',
                    email: data?.email || '',
                    phoneNumber: data?.phoneNumber || '',
                    notes: data?.notes || '',
                    vehicles: (data?.vehicles || []).map((v) => ({
                        vehicleMake: v?.vehicleMake || '',
                        licensePlate: v?.licensePlate || '',
                        province: v?.province || '',
                        model: v?.model || '',
                        year: v?.year || '',
                        color: v?.color || '',
                        id: v?._id || ''
                    }))
                };
                setInitialValues(mappedData);
            }
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((editMode && parkerId) || viewMode) {
            fetchParker(parkerId);
        }
    }, [editMode, parkerId, viewMode]);

    useEffect(() => {
        if (!show) {
            setStep('options');
            setInitialValues(initialValueObj);
        }
    }, [show]);
    // Fetch subscription plans for the group admin on mount or when groupId changes

    const fetchSubscriptions = async () => {
        const response = await axiosWrapper(
            'get',
            `${API_URL.GET_GROUP_ADMIN_SUBSCRIPTION_BY_ID(groupId)}`,
            null,
            userToken
        );

        if (response?.success) {
            // Map the response to options: value as subscriptionId, label as "name - frequency"
            const options = response?.data.map((sub) => ({
                value: sub?._id,
                label: `${sub?.name} - ${sub?.frequency}`
            }));
            setSubscriptionOptions(options);
        }
    };

    useEffect(() => {
        if (groupId) fetchSubscriptions();
    }, [groupId]);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="add-parker-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 'options' && 'Add Parkers'}
                    {step === 'invite' && 'Invite Parkers'}
                    {step === 'manual' && `${editMode ? 'Update' : 'Add'} Parker Manually`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {step === 'options' && (
                    <div className="options-container">
                        <p>Choose your preferred method of adding parkers</p>
                        <div className="option-card" onClick={() => setStep('invite')}>
                            <div className="option-icon">
                                <img src={inviteIcon || '/placeholder.svg'} alt="invite icon" />
                            </div>
                            <div className="option-content">
                                <h6>Invite Parkers (recommended)</h6>
                                <p>
                                    Once invited, parkers can add and manage their own vehicles, so you don't have to.
                                </p>
                            </div>
                        </div>
                        <div className="option-card" onClick={() => setStep('manual')}>
                            <div className="option-icon">
                                <img src={manualIcon || '/placeholder.svg'} alt="manual icon" />
                            </div>
                            <div className="option-content">
                                <h6>Manually Add</h6>
                                <p>
                                    Add parkers without inviting them. You'll have to keep up with any changes to their
                                    account or vehicles.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'invite' && (
                    <div className="invite-container">
                        <p>
                            Invite parkers via email. Once they sign up and register their vehicle using their invite
                            link, they'll be added to your subscription.
                        </p>

                        <Formik
                            initialValues={inviteInitialValues}
                            validationSchema={inviteValidationSchema}
                            onSubmit={handleInviteSubmit}
                            enableReinitialize
                        >
                            {({ values, setFieldValue, isSubmitting }) => (
                                <Form>
                                    <div className="email-input-container">
                                        <label>Enter Parkers' Emails</label>
                                        <CreatableSelect
                                            isMulti
                                            name="emails"
                                            placeholder="Enter or copy email addresses"
                                            value={values.emails}
                                            onChange={(value) => setFieldValue('emails', value)}
                                            className="multi-select-field"
                                            classNamePrefix="__select-days"
                                        />
                                        <ErrorMessage name="emails" component="div" className="error-message" />
                                    </div>

                                    {(userInfo?.role === 'OPERATOR' || userInfo?.role === 'ADMIN') && !editMode && (
                                        <>
                                            <div className="form-group">
                                                <label>Select Subscription Plan(s)</label>
                                                <Select
                                                    isMulti
                                                    name="subscriptions"
                                                    placeholder="Select subscription plan(s)"
                                                    options={subscriptionOptions}
                                                    value={subscriptionOptions.filter((option) =>
                                                        values?.subscriptions?.includes(option.value)
                                                    )}
                                                    onChange={(selectedOptions) =>
                                                        setFieldValue(
                                                            'subscriptions',
                                                            selectedOptions
                                                                ? selectedOptions.map((option) => option.value)
                                                                : []
                                                        )
                                                    }
                                                    className="multi-select-field"
                                                    classNamePrefix="__select-days"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="notrs">Notes</label>
                                                <Field
                                                    readOnly={viewMode}
                                                    name="notes"
                                                    type="text"
                                                    placeholder="e.g. Free"
                                                />
                                                <ErrorMessage name="notes" component="div" className="error-message" />
                                            </div>
                                        </>
                                    )}
                                    <div className="button-container">
                                        <button
                                            type="button"
                                            className="back-button"
                                            onClick={() => setStep('options')}
                                            disabled={isSubmitting}
                                        >
                                            Back
                                        </button>
                                        <button type="submit" className="send-button" disabled={isSubmitting}>
                                            {isSubmitting ? <Spinner animation="border" size="sm" /> : `Send Invite`}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}

                {step === 'manual' && (
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleManualSubmit}
                        enableReinitialize
                    >
                        {({ values, setFieldValue, isSubmitting, resetForm, setSubmitting }) => (
                            <Form className="manual-form">
                                <div className="personal-info">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="firstName">First Name*</label>
                                            <Field
                                                readOnly={viewMode}
                                                name="firstName"
                                                type="text"
                                                placeholder="e.g. John"
                                            />
                                            <ErrorMessage name="firstName" component="div" className="error-message" />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="lastName">Last Name*</label>
                                            <Field
                                                readOnly={viewMode}
                                                name="lastName"
                                                type="text"
                                                placeholder="e.g. Doe"
                                            />
                                            <ErrorMessage name="lastName" component="div" className="error-message" />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="email">Email</label>
                                            <Field
                                                readOnly={viewMode}
                                                name="email"
                                                type="email"
                                                placeholder="Enter email address"
                                            />
                                            <ErrorMessage name="email" component="div" className="error-message" />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="phoneNumber">Phone Number</label>
                                            <PhoneInput
                                                country={'us'}
                                                value={values.phoneNumber}
                                                onChange={(phone) => setFieldValue('phoneNumber', `+${phone}`)}
                                                placeholder="Enter phone number"
                                                inputClass="phone-input"
                                                countryCodeEditable={false}
                                                enableSearch={true}
                                                searchPlaceholder="Search"
                                                disabled={viewMode}
                                            />
                                            <ErrorMessage
                                                name="phoneNumber"
                                                component="div"
                                                className="error-message"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="notrs">Notes</label>
                                            <Field
                                                readOnly={viewMode}
                                                name="notes"
                                                type="text"
                                                placeholder="e.g. Free"
                                            />
                                            <ErrorMessage name="notes" component="div" className="error-message" />
                                        </div>
                                    </div>
                                </div>

                                {(userInfo?.role === 'OPERATOR' || userInfo?.role === 'ADMIN') && !editMode && (
                                    <>
                                        <div className="form-group">
                                            <label>Select Subscription Plan(s)</label>
                                            <Select
                                                isMulti
                                                name="subscriptions"
                                                placeholder="Select subscription plan(s)"
                                                options={subscriptionOptions}
                                                value={subscriptionOptions.filter((option) =>
                                                    values?.subscriptions?.includes(option.value)
                                                )}
                                                onChange={(selectedOptions) =>
                                                    setFieldValue(
                                                        'subscriptions',
                                                        selectedOptions
                                                            ? selectedOptions.map((option) => option.value)
                                                            : []
                                                    )
                                                }
                                                className="multi-select-field"
                                                classNamePrefix="__select-days"
                                            />
                                        </div>
                                    </>
                                )}
                                <h5>Add Vehicle(s)</h5>
                                <FieldArray name="vehicles">
                                    {({ push, remove }) => (
                                        <div>
                                            {values.vehicles.map((vehicle, index) => (
                                                <div key={index} className="vehicle-form">
                                                    {index > 0 && (
                                                        <IoIosCloseCircleOutline
                                                            type="button"
                                                            className="remove-vehicle"
                                                            onClick={() => remove(index)}
                                                        />
                                                    )}
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>Vehicle Make*</label>
                                                            <Field
                                                                name={`vehicles.${index}.vehicleMake`}
                                                                type="text"
                                                                placeholder="e.g. Honda"
                                                                readOnly={viewMode}
                                                            />
                                                            <ErrorMessage
                                                                name={`vehicles.${index}.vehicleMake`}
                                                                component="div"
                                                                className="error-message"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>License Plate #*</label>
                                                            <Field name={`vehicles.${index}.licensePlate`}>
                                                                {({ field }) => (
                                                                    <input
                                                                        {...field}
                                                                        type="text"
                                                                        readOnly={viewMode}
                                                                        onChange={(e) => {
                                                                            const upperCaseVal =
                                                                                e.target.value.toUpperCase();
                                                                            setFieldValue(
                                                                                `vehicles.${index}.licensePlate`,
                                                                                upperCaseVal
                                                                            );
                                                                        }}
                                                                        value={
                                                                            field.value ? field.value.toUpperCase() : ''
                                                                        }
                                                                    />
                                                                )}
                                                            </Field>
                                                            <ErrorMessage
                                                                name={`vehicles.${index}.licensePlate`}
                                                                component="div"
                                                                className="error-message"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>Province/State*</label>
                                                            <Select
                                                                options={states}
                                                                className="multi-select-field"
                                                                classNamePrefix="__select-days"
                                                                placeholder="Select"
                                                                value={
                                                                    states.find(
                                                                        (state) =>
                                                                            state.value ===
                                                                            values.vehicles[index].province
                                                                    ) || null
                                                                }
                                                                onChange={(option) =>
                                                                    setFieldValue(
                                                                        `vehicles.${index}.province`,
                                                                        option.value
                                                                    )
                                                                }
                                                                isDisabled={viewMode}
                                                            />

                                                            <ErrorMessage
                                                                name={`vehicles.${index}.province`}
                                                                component="div"
                                                                className="error-message"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Model*</label>
                                                            <Field
                                                                name={`vehicles.${index}.model`}
                                                                type="text"
                                                                placeholder="Select car model"
                                                                readOnly={viewMode}
                                                            />
                                                            <ErrorMessage
                                                                name={`vehicles.${index}.model`}
                                                                component="div"
                                                                className="error-message"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>Year*</label>
                                                            <Field
                                                                name={`vehicles.${index}.year`}
                                                                type="number"
                                                                placeholder="Select car year"
                                                                min="1886"
                                                                max={currentYear + 1}
                                                                readOnly={viewMode}
                                                            />
                                                            <ErrorMessage
                                                                name={`vehicles.${index}.year`}
                                                                component="div"
                                                                className="error-message"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Color*</label>
                                                            <Field
                                                                name={`vehicles.${index}.color`}
                                                                type="text"
                                                                placeholder="Select color"
                                                                readOnly={viewMode}
                                                            />
                                                            <ErrorMessage
                                                                name={`vehicles.${index}.color`}
                                                                component="div"
                                                                className="error-message"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    disabled={viewMode}
                                                    className="add-vehicle-button"
                                                    onClick={() =>
                                                        push({
                                                            vehicleMake: '',
                                                            licensePlate: '',
                                                            province: '',
                                                            model: '',
                                                            year: '',
                                                            color: ''
                                                        })
                                                    }
                                                >
                                                    Add another Vehicle
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </FieldArray>
                                <div className="button-container">
                                    {!viewMode && (
                                        <button
                                            type="button"
                                            className="back-button"
                                            onClick={editMode ? () => onHide() : () => setStep('options')}
                                        >
                                            Back
                                        </button>
                                    )}
                                    <div className="right-buttons">
                                        {!editMode && (
                                            <button
                                                type="button"
                                                className="save-and-add-button"
                                                onClick={() =>
                                                    handleManualSubmitAndAdd(values, { setSubmitting, resetForm })
                                                }
                                                disabled={isSubmitting || viewMode}
                                            >
                                                Save & Add Another
                                            </button>
                                        )}
                                        {!viewMode && (
                                            <button type="submit" className="save-button" disabled={isSubmitting}>
                                                {isSubmitting ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    `${editMode ? 'Update' : 'Save'} & Close`
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AddParkerModal;
